// @ts-strict-ignore
import React from 'react'
import classNames from 'classnames'
import { IonGrid, IonRow, IonContent } from '@ionic/react'
import {
	SCREEN_SECTION_TYPE_AUDIO,
	SCREEN_SECTION_TYPE_VIDEO,
	SCREEN_SECTION_TYPE_IMAGE,
	SCREEN_SECTION_TYPE_WEB_VIDEO,
	SCREEN_SECTION_TYPE_ANIMATION,
	SCREEN_SECTION_TYPE_AR,
	ScreenSection,
	Screen,
} from 'mytours-read-common'
import { MediaItem } from 'mytours-read-api-typescript'
import SectionMediaImage from './SectionMediaImage'
import SectionMediaAr from './SectionMediaAr'
import strings from '../../../../strings'
import { Button, Text } from '@mytours/components'
import Slider, { SwiperClass } from './Slider'
import { getDirMode } from 'mobile_web/modules/platform'
import '../styles/fullscreenMedia.less'
import { isWideImage } from 'mobile_web/modules/platform'
import WithMediaQuery, { UseMediaArgs } from 'mobile_web/modules/common/hocs/media-query'
import { getImageAlt } from 'mobile_web/modules/common/functions'
import SectionMediaGroupVideoComponent from './SectionMediaGroupVideo'
import SectionMediaAudioComponent from './SectionMediaAudio'

interface Props {
	onBack: () => void
	mediaData: MediaItem[]
	activeSlideIndex?: number
	section: ScreenSection
	collectionId?: number
	screen: Screen
}

interface BreakpointProps {
	isBigDesktop?: boolean
	isMediumDesktop?: boolean
	isDesktop?: boolean
	isTablet?: boolean
	isMobile?: boolean
}

interface State {
	loading: boolean
	activeIndex: number
	showAttribution: boolean
	displayInfo: boolean
	displayCaptionOverflowShadow: boolean
}

const defaultSliderProps = {
	loop: true,
	enableControls: true,
	slideOptions: { shortSwipes: true, slidesPerView: 1 },
}
class FullscreenMedia extends React.Component<Props & BreakpointProps, State> {
	slidesRef: SwiperClass | null
	captionRef: HTMLDivElement | null

	state: State = {
		loading: true,
		activeIndex: 0,
		showAttribution: false,
		displayInfo: true,
		displayCaptionOverflowShadow: false,
	}

	constructor(props: Props & BreakpointProps) {
		super(props)

		this.state = {
			loading: true,
			activeIndex: props.activeSlideIndex || 0,
			showAttribution: false,
			displayInfo: true,
			displayCaptionOverflowShadow: false,
		}
	}

	componentDidMount() {
		document.addEventListener('click', this.handleClickOutside)

		if (this.captionRef && !this.state.displayCaptionOverflowShadow) {
			setTimeout(() => {
				if (this.captionRef.scrollHeight > this.captionRef.clientHeight) {
					this.setState({ displayCaptionOverflowShadow: true })
				}
			}, 0)
		}
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleClickOutside)
	}

	getSlidesRef: React.RefCallback<SwiperClass> = slidesRef => {
		this.slidesRef = slidesRef
	}

	handleClickOutside = (event: any) => {
		const { target } = event

		if (
			!target?.classList?.contains('media-action-click') &&
			!target?.parentNode?.classList?.contains('media-action-click')
		) {
			this.toggleDisplayInfo(event)
		}
	}

	private get currentItem(): MediaItem | null {
		const { mediaData, activeSlideIndex } = this.props
		const { activeIndex } = this.state
		if (!mediaData?.length) {
			return null
		}

		const index =
			activeIndex > -1 && activeIndex !== undefined
				? activeIndex
				: activeSlideIndex > -1 && activeSlideIndex !== undefined
					? activeSlideIndex
					: undefined

		if (index === undefined) {
			return null
		}

		return mediaData[index] || null
	}

	renderItem = (sectionItem: MediaItem, index?: number, isSlider?: boolean): JSX.Element => {
		let sectionItemComponent: JSX.Element
		const { onBack, mediaData, section, collectionId, screen } = this.props
		const { activeIndex } = this.state

		switch (sectionItem.media_type) {
			case SCREEN_SECTION_TYPE_AUDIO:
				sectionItemComponent = (
					<SectionMediaAudioComponent
						collectionId={collectionId}
						container={{ id: section.id, type: 'section', title: section.name }}
						audioPreview={sectionItem}
						height="100%"
						isFullscreenSlider={true}
						screen={screen}
					/>
				)
				break
			case SCREEN_SECTION_TYPE_VIDEO:
			case SCREEN_SECTION_TYPE_WEB_VIDEO: {
				const hasPrev = index && index > 0
				const hasNext = index < mediaData.length - 1
				const componentProps = {
					sliderRef: this.slidesRef,
					slideIndex: index,
					sectionItem,
					hasPrev,
					hasNext,
					onBack,
					autoplay: !isSlider,
					activeIndex: activeIndex,
					isInLoopSlider: defaultSliderProps.loop,
					section,
					screen,
				}
				sectionItemComponent = (
					// @ts-ignore Property 'sliderRef/onBack/hasPrev/hasNext/slideIndex' does not exist (native only prop)
					<SectionMediaGroupVideoComponent {...componentProps} />
				)
				break
			}
			case SCREEN_SECTION_TYPE_IMAGE:
			case SCREEN_SECTION_TYPE_ANIMATION: {
				const { isBigDesktop, isMediumDesktop, isDesktop, isTablet, isMobile } = this.props

				const wideImage = isWideImage(sectionItem.file)
				let width, height

				if (isBigDesktop) {
					width = 1500
					height = 950
				} else if (isMediumDesktop) {
					width = 1400
					height = 900
				} else if (isDesktop) {
					width = 1200
					height = 760
				} else if (isTablet) {
					width = 1024
					height = 810
				} else if (isMobile) {
					width = 768
					height = 800
				}

				sectionItemComponent = (
					<SectionMediaImage
						file={sectionItem.file}
						alt={getImageAlt(sectionItem)}
						width={wideImage ? width : undefined}
						height={!wideImage ? height : undefined}
						withLoader={true}
					/>
				)
				break
			}
			case SCREEN_SECTION_TYPE_AR:
				sectionItemComponent = <SectionMediaAr sectionItem={sectionItem} />
				break
			default:
				console.warn(`sectionItem Type : ${sectionItem.media_type}`)
				sectionItemComponent = <div />
				break
		}

		return sectionItemComponent
	}

	handleSlideChange = (activeIndex: number): void => {
		const realIndex = this.slidesRef?.realIndex
		const index = realIndex > -1 ? realIndex : activeIndex
		this.setState({ activeIndex: index, showAttribution: false })
	}

	toggleAttribution = (): void => {
		this.setState((prev: State) => ({ showAttribution: !prev.showAttribution }))
	}

	toggleDisplayInfo: React.MouseEventHandler<HTMLIonContentElement> = () => {
		this.setState(prevState => {
			if (prevState.displayInfo && this.state.showAttribution) {
				// hide attribution when click on background
				this.toggleAttribution()
			}

			return { displayInfo: !prevState.displayInfo }
		})
	}

	renderAttribution = (): React.ReactNode => (
		<IonGrid className={classNames('tooltip', { 'show-tooltip': this.state.showAttribution })} data-id="tooltip">
			<div dangerouslySetInnerHTML={{ __html: this.currentItem?.attribution || '' }} />
		</IonGrid>
	)

	renderAttributionButton = (): React.ReactNode =>
		this.currentItem.attribution && (
			<>
				<Button
					variant="clear"
					className="fullscreen-media-action info-button media-action-click"
					data-id="tooltip"
					onClick={this.toggleAttribution}
					aria-label={strings.info_title}
				>
					<i className="fal fa-info-circle info-icon" />
				</Button>
				{this.renderAttribution()}
			</>
		)

	renderHeader = (): React.ReactNode => {
		const { onBack, mediaData } = this.props
		const { activeIndex } = this.state

		const currentItem = this.currentItem

		if (!currentItem) {
			return null
		}

		return (
			<IonRow className="media-header">
				<Text variant="inherit" className="count-info media-action-click" dir={getDirMode()}>
					{strings.formatString(strings.n_of_total, { n: activeIndex + 1, total: mediaData.length })}
				</Text>

				<IonRow className="media-actions">
					{this.renderAttributionButton()}
					<Button
						variant="clear"
						className="fullscreen-media-action back-button media-action-click"
						onClick={onBack}
						aria-label={strings.accessibility_go_back}
					>
						<i className="fal fa-xmark close-icon" />
					</Button>
				</IonRow>
			</IonRow>
		)
	}

	renderContent = (): React.ReactNode | null => {
		const { mediaData, activeSlideIndex } = this.props

		const currentItem = this.currentItem

		if (mediaData.length > 1) {
			return (
				<Slider
					data={mediaData}
					getRef={this.getSlidesRef}
					className="full-media-slider"
					controlsClassName="full-media-slider-controls"
					getActiveSliderIndex={this.handleSlideChange}
					renderItem={(section, index) => this.renderItem(section, index, true)}
					initialSlide={activeSlideIndex}
					onSlideClick={activeIndex => this.setState({ activeIndex })}
					{...defaultSliderProps}
				/>
			)
		} else if (currentItem) {
			return this.renderItem(currentItem)
		}
		return null
	}

	renderFooter = (): React.ReactNode => {
		const caption = this.currentItem?.caption

		return (
			<div
				className={classNames('footer caption', { 'overflow-shadow': this.state.displayCaptionOverflowShadow })}
				dir={getDirMode()}
				ref={ref => (this.captionRef = ref)}
			>
				{caption ? (
					<div className="inner">
						<div className="inner-text" dangerouslySetInnerHTML={{ __html: caption || '' }} />
					</div>
				) : (
					<></>
				)}
			</div>
		)
	}

	render(): React.ReactNode {
		const { mediaData } = this.props
		const { activeIndex, displayInfo } = this.state

		const currentItem: MediaItem = activeIndex > -1 && mediaData ? mediaData[activeIndex] : null

		if (!currentItem) {
			return null
		}

		return (
			<IonContent
				className={classNames('media-fullscreen-content', { 'display-info': displayInfo })}
				data-cy="fullscreen-media-container"
			>
				{this.renderHeader()}
				{this.renderContent()}
				{this.renderFooter()}
			</IonContent>
		)
	}
}

const mediaQueries: UseMediaArgs = {
	isBigDesktop: [{ minWidth: 1440 }],
	isMediumDesktop: [{ maxWidth: 1440, minWidth: 1200 }],
	isDesktop: [{ maxWidth: 1200, minWidth: 1024 }],
	isTablet: [{ maxWidth: 1024, minWidth: 768 }],
	isMobile: [{ maxWidth: 768 }],
}

export default WithMediaQuery(mediaQueries)(FullscreenMedia)
